import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import type { RegionSelectValueType } from '@/pages/home/components/NewRegionSelect/type';
import { SELECTED_OPTION_CHECKBOX } from '@/pages/home/components/NewRegionSelect/config';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import type { IBatchAddFieldPopoverProps } from './types';
import {
  applyBatchFields,
  buildBatchFieldKeyMap,
  buildOpenSnapshotKeys,
} from './utils/applyBatchFields';
import { sanitizeSelectedMarkets } from './utils/batchFieldDisableRules';
import {
  buildRegionSelectOptions,
  buildRegionSelectValueFromColumns,
  getRealRegionsList,
  resolveDefaultSelectedRegion,
} from './utils/buildRegionSelectOptions';
import type { IColumnRawInfo } from '@dbl/core/dataSource';
import SvgIcon from '@home/components/SvgIcon';

import './index.scss';

/**
 * 我的分析操作区「批量添加字段」入口。
 *
 * 交互约定：
 * - 复用 NewRegionSelect 级联多选 UI；
 * - 勾选过程只更新本地 draft，不 dispatch model；
 * - 关闭下拉时 diff「打开快照 vs 最终勾选」并写回 Sortable 操作区。
 *
 * 状态分层：
 * - openSnapshotKeys：打开瞬间操作区字段 key，用于 diff 与 disable 中的 retained 计算；
 * - draftMarkets / draftValueRef：下拉展开期间的勾选草稿；
 * - value：受控同步 NewRegionSelect，全选后须回写以剔除非法项。
 */
const BatchAddFieldPopover: React.FC<IBatchAddFieldPopoverProps> = ({
  config,
  isDragging,
  dragSuffixText = '',
}) => {
  const { dblFormatMessage: formatMessage, locale } = useDblIntl('multi');
  const { dblFormatMessage: dragFormatMessage } = useDblIntl('sortable_drag');
  const {
    targetKey,
    dataTables,
    customColumns,
    existingColumns,
    isEn,
    chartType,
  } = config;

  const customFieldLabel = formatMessage('batch_add_custom_field');
  /** 拖拽时递增 key 强制卸载下拉，避免浮层与 Sortable 并存 */
  const [dropdownKey, setDropdownKey] = useState<number>(0);
  /** 打开下拉时的字段 key 快照，供 disable 规则与关闭写回 diff */
  const [openSnapshotKeys, setOpenSnapshotKeys] = useState<Set<string>>(
    () => new Set(),
  );
  /** 下拉展开期间的勾选 key，驱动 options 动态 disabled */
  const [draftMarkets, setDraftMarkets] = useState<string[]>(() =>
    buildRegionSelectValueFromColumns(existingColumns, targetKey).markets || [],
  );
  /** 下拉展开期间的勾选草稿；关闭时才写回 model */
  const draftValueRef = useRef<RegionSelectValueType>(
    buildRegionSelectValueFromColumns(existingColumns, targetKey),
  );
  /** 打开下拉时预构建，关闭写回时传入 applyBatchFields 避免重复 buildFieldTree */
  const fieldKeyMapRef = useRef<Map<string, IColumnRawInfo> | null>(null);
  /** 受控 value 同步勾选草稿，全选后需回写以剔除非法项 */
  const [value, setValue] = useState<RegionSelectValueType>(() =>
    buildRegionSelectValueFromColumns(existingColumns, targetKey),
  );
  /** 打开下拉时透传 NewRegionSelect.defaultSelectedRegion，驱动左侧默认高亮 */
  const [defaultSelectedRegion, setDefaultSelectedRegion] = useState<string | undefined>();
  /**
   * 脉冲 loading 触发公共组件内部 selectedRegion 同步（其仅随 loading 变化更新）。
   * flushSync 双次切换，避免下拉内出现 loading 闪烁。
   */
  const [regionSelectLoading, setRegionSelectLoading] = useState(false);

  const validExistingColumns = useMemo(
    () => existingColumns || [],
    [existingColumns],
  );

  /** 勾选 key 集合，传入 disable 规则以动态计算「再选 1 个是否超限」 */
  const draftSelectedKeys = useMemo(
    () => new Set(draftMarkets),
    [draftMarkets],
  );

  /** 随 draftMarkets 重算 options.disabled，与 sanitize 使用同一套 evaluateBatchFieldDisable */
  const regionSelectOptions = useMemo(
    () =>
      buildRegionSelectOptions({
        dataTables,
        customColumns,
        targetKey,
        isEn,
        customFieldLabel,
        chartType,
        disableContext: {
          targetKey,
          chartType,
          existingColumns: validExistingColumns,
          selectedKeys: draftSelectedKeys,
          openSnapshotKeys,
        },
      }),
    [
      chartType,
      customColumns,
      customFieldLabel,
      dataTables,
      draftSelectedKeys,
      isEn,
      openSnapshotKeys,
      targetKey,
      validExistingColumns,
    ],
  );

  /** sanitize 共用上下文（不含 selectedKeys，由 sanitizeSelectedMarkets 内部增量维护） */
  const disableBaseContext = useMemo(
    () => ({
      targetKey,
      chartType: chartType || '',
      existingColumns: validExistingColumns,
      openSnapshotKeys,
    }),
    [chartType, openSnapshotKeys, targetKey, validExistingColumns],
  );

  const realRegionsList = useMemo(
    () => getRealRegionsList(regionSelectOptions),
    [regionSelectOptions],
  );

  // 拖拽进行中强制关闭下拉，避免与 Sortable 事件冲突
  useEffect(() => {
    if (isDragging) {
      setDropdownKey(prev => prev + 1);
    }
  }, [isDragging]);

  /** 下拉展开时重置快照与勾选态；关闭时不改 value，由 onCloseReginSelect 统一写回 */
  const handleOpenChange = useCallback(
    (open: boolean) => {
      // 仅处理展开；收起由 onCloseReginSelect 负责写回，避免重复 apply
      if (!open) {
        return;
      }
      const snapshot = buildOpenSnapshotKeys(validExistingColumns);
      setOpenSnapshotKeys(snapshot);
      const initialValue = buildRegionSelectValueFromColumns(
        validExistingColumns,
        targetKey,
      );
      // 每次打开重置 draft，避免上次未关闭的勾选污染本次
      draftValueRef.current = initialValue;
      setDraftMarkets(initialValue.markets || []);
      setValue(initialValue);
      const nextDefaultRegion = resolveDefaultSelectedRegion(
        regionSelectOptions,
        initialValue.markets || [],
      );
      // 须与 loading 脉冲同批 commit，公共组件才能在 loading 回落时读到 defaultSelectedRegion
      flushSync(() => {
        setDefaultSelectedRegion(nextDefaultRegion);
        setRegionSelectLoading(true);
      });
      flushSync(() => {
        setRegionSelectLoading(false);
      });
      // 预构建 key→rawField，关闭写回时复用，避免重复遍历 dataTables
      fieldKeyMapRef.current = buildBatchFieldKeyMap(
        dataTables,
        customColumns,
        isEn,
        customFieldLabel,
      );
    },
    [
      customColumns,
      customFieldLabel,
      dataTables,
      isEn,
      regionSelectOptions,
      targetKey,
      validExistingColumns,
    ],
  );

  /** 勾选过程增量校验并同步受控 value，避免右侧全选绕过动态 disable 规则 */
  const handleChange = useCallback(
    (next: RegionSelectValueType | null) => {
      // NewRegionSelect 清空勾选时 markets 为空数组
      if (!next) {
        draftValueRef.current = {
          type: SELECTED_OPTION_CHECKBOX,
          regions: [],
          markets: [],
        };
        setDraftMarkets([]);
        setValue(draftValueRef.current);
        return;
      }
      // 全选一次性写入大量 key，须按顺序增量校验；不能依赖上一轮 options 的 disabled 快照
      const sanitizedMarkets = sanitizeSelectedMarkets(
        next.markets || [],
        disableBaseContext,
        fieldKeyMapRef.current,
      );
      const sanitizedValue = { ...next, markets: sanitizedMarkets };
      draftValueRef.current = sanitizedValue;
      setDraftMarkets(sanitizedMarkets);
      // 回写受控 value，否则 NewRegionSelect 内部仍显示全选后的非法勾选
      setValue(sanitizedValue);
    },
    [disableBaseContext],
  );

  /** 仅在下拉关闭时 diff 并写回操作区；rAF 让下拉先完成关闭动画再跑重逻辑 */
  const handleCloseRegionSelect = useCallback(
    (next: RegionSelectValueType | null) => {
      // onCloseReginSelect 可能传入 null，回退到 draftValueRef 最后一次合法勾选
      const finalValue = next || draftValueRef.current;
      // 关闭路径再次 sanitize，兜底 onChange 未触发的边界（如直接点遮罩关闭）
      const sanitizedMarkets = sanitizeSelectedMarkets(
        finalValue?.markets || [],
        disableBaseContext,
        fieldKeyMapRef.current,
      );
      const selectedKeys = new Set(sanitizedMarkets);
      const runApply = (): void => {
        applyBatchFields({
          selectedKeys,
          openSnapshotKeys,
          targetKey,
          existingColumns: validExistingColumns,
          ctx: {
            ...config,
            fieldKeyMap: fieldKeyMapRef.current ?? undefined,
          },
          formatMessage,
        });
        fieldKeyMapRef.current = null;
      };
      // rAF：先让下拉完成关闭动画，再跑 buildTree / persist 等重逻辑，减轻卡顿
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(runApply);
        return;
      }
      runApply();
    },
    [
      config,
      disableBaseContext,
      formatMessage,
      openSnapshotKeys,
      targetKey,
      validExistingColumns,
    ],
  );

  const triggerButton = (
    <span className="dbl-batch-add-popover__trigger">
      <SvgIcon type={'cross'}/>
      {`${dragFormatMessage('batch_add_or_drag_drop')}${dragSuffixText}`}
    </span>
  );

  /** 透传 tea Dropdown onOpenChange，不修改 NewRegionSelect 公共组件 */
  const dropdownControlProps: Record<string, unknown> = {
    onOpenChange: handleOpenChange,
  };

  return (
    <div
      className="dbl-batch-add-popover-select"
      onClick={event => event.stopPropagation()}
    >
      <NewRegionSelect
        key={dropdownKey}
        className="dbl-batch-add-popover-cascader"
        boxClassName="dbl-batch-add-popover-cascader-box"
        value={value}
        onChange={handleChange}
        onCloseReginSelect={handleCloseRegionSelect}
        locale={locale}
        options={regionSelectOptions}
        radioOptions={[]}
        realRegionsList={realRegionsList}
        closeOnScroll={false}
        shortcutOptions={[]}
        popularList={[]}
        selectFilterType="batch_add_field"
        firstLevelTitle={formatMessage('batch_add_table')}
        rightListWidth={359}
        /** 字段类型 icon 为 SvgIcon，非国家/地区图片 */
        isNotRegionIcon
        button={triggerButton}
        defaultSelectedRegion={defaultSelectedRegion}
        loading={regionSelectLoading}
        {...dropdownControlProps}
      />
    </div>
  );
};

export default BatchAddFieldPopover;
